import { validateAssemblyOptionsSubmit } from '#/modules/imsMgmt/settings/mtr/helper/assemblyOptionsSerialize';

export function parseOriginalSerialListText(text) {
  if (!text?.trim()) {
    return [];
  }

  return text
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function formatOriginalSerialListText(list) {
  if (!Array.isArray(list) || list.length === 0) {
    return '';
  }

  return list.join(', ');
}

/**
 * detail.specOption／specInput 列上的規格 id。
 * 後端可能給巢狀 mtrSpec、扁平 mtrSpecId，或直接用列上的 id。
 */
export function resolveSpecId(item) {
  return item?.mtrSpec?.id ?? item?.mtrSpecId ?? item?.id ?? '';
}

/**
 * detail.specOption 列上的選中 option 物件。
 */
export function resolveSpecOption(item) {
  return item?.mtrSpecOption ?? item?.selectedOption ?? item?.option ?? null;
}

/**
 * detail.specOption 列上的選中 option id。
 * 後端可能給 mtrSpecOptionId、巢狀 mtrSpecOption／selectedOption／option。
 */
export function resolveOptionId(item) {
  return item?.mtrSpecOptionId
    ?? resolveSpecOption(item)?.id
    ?? '';
}

function formatSpecOptionChoiceLabel(option, fallbackId = '') {
  if (!option) {
    return fallbackId ? String(fallbackId) : '';
  }

  const code = option.code;
  const name = option.name ?? option.label;
  const label = [code, name].filter(Boolean).join(' - ');
  return label || String(option.id ?? fallbackId);
}

/**
 * detail.specOption → FloatingSelect 種子（specId → [{ value, label }]）。
 * 在 mtrSpec detail options 尚未載入時，仍可顯示已選 option。
 */
export function buildSpecSelectOptionsSeed(specOption = []) {
  const map = {};

  (specOption ?? []).forEach((item) => {
    const specId = resolveSpecId(item);
    const option = resolveSpecOption(item);
    const optionId = resolveOptionId(item);
    if (!specId || !optionId) {
      return;
    }

    const list = map[specId] ?? [];
    if (!list.some((choice) => String(choice.value) === String(optionId))) {
      list.push({
        value: optionId,
        label: formatSpecOptionChoiceLabel(option, optionId),
      });
    }
    map[specId] = list;
  });

  return map;
}

/**
 * detail 三組 spec → 表單用 bySpecId + specCustom
 */
export function parseDetailSpecValues(detail) {
  const bySpecId = {};

  (detail?.specOption ?? []).forEach((item) => {
    const specId = resolveSpecId(item);
    if (!specId) {
      return;
    }

    bySpecId[specId] = {
      ...bySpecId[specId],
      mtrSpecOptionId: resolveOptionId(item),
    };
  });

  (detail?.specInput ?? []).forEach((item) => {
    const specId = resolveSpecId(item);
    if (!specId) {
      return;
    }

    bySpecId[specId] = {
      ...bySpecId[specId],
      inputValue: item?.inputValue ?? '',
    };
  });

  const specCustom = (detail?.specCustom ?? []).map((item) => ({
    customKey: item?.customKey ?? '',
    inputValue: item?.inputValue ?? '',
  }));

  return { bySpecId, specCustom };
}

/**
 * 模板 rules + 既有值 → specEntries 陣列
 */
export function buildSpecEntriesFromTemplate(templateDetail, bySpecId = {}) {
  const rules = [...(templateDetail?.specs ?? [])].sort(
    (left, right) => (left.displayOrder ?? 0) - (right.displayOrder ?? 0),
  );

  return rules.map((rule) => {
    const mtrSpecId = rule.mtrSpec?.id ?? rule.mtrSpecId ?? '';
    const specType = rule.mtrSpec?.specType ?? 'INPUT';
    const existing = bySpecId[mtrSpecId] ?? {};
    const code = rule.mtrSpec?.code ?? '';
    const name = rule.mtrSpec?.name ?? '';

    return {
      mtrSpecId,
      specType,
      required: Boolean(rule.required),
      code,
      name,
      /** 驗證文案用；僅 name，勿組 code - name */
      label: name || code || '',
      mtrSpecOptionId: existing.mtrSpecOptionId ?? '',
      inputValue: existing.inputValue ?? '',
    };
  });
}

/**
 * 表單 specEntries + specCustom → API spec_values[]
 */
export function serializeSpecValues(specEntries = [], specCustom = []) {
  const values = [];

  specEntries.forEach((entry) => {
    if (entry.specType === 'SELECT' && entry.mtrSpecOptionId) {
      values.push({
        mtrSpecId: entry.mtrSpecId,
        mtrSpecOptionId: entry.mtrSpecOptionId,
        customKey: null,
        inputValue: null,
      });
      return;
    }

    if (entry.specType === 'INPUT' && entry.inputValue?.trim()) {
      values.push({
        mtrSpecId: entry.mtrSpecId,
        mtrSpecOptionId: null,
        customKey: null,
        inputValue: entry.inputValue.trim(),
      });
    }
  });

  specCustom.forEach((row) => {
    if (row.customKey?.trim() && row.inputValue?.trim()) {
      values.push({
        mtrSpecId: null,
        mtrSpecOptionId: null,
        customKey: row.customKey.trim(),
        inputValue: row.inputValue.trim(),
      });
    }
  });

  return values;
}

export function validateMtrSubmit(values, mode) {
  const messages = [];
  const requiresActiveFields = mode === 'active' || mode === 'activate';

  if (requiresActiveFields) {
    if (!values.mtrNo?.trim()) {
      messages.push({ path: 'mtrNo', message: '啟用時料號不能為空' });
    }

    if (values.mtrKind !== 'GROUP') {
      const hasBase = Boolean(values.baseUnitName?.trim());
      if (!hasBase) {
        messages.push({ path: 'baseUnitName', message: '啟用時請填寫基本單位名稱' });
      }
    }
  }

  (values.specEntries ?? []).forEach((entry, index) => {
    if (!entry.required) {
      return;
    }

    const specLabel = entry.name || entry.label || '規格';

    if (entry.specType === 'SELECT' && !entry.mtrSpecOptionId) {
      messages.push({
        path: `specEntries.${index}.mtrSpecOptionId`,
        message: `${specLabel}為必填`,
      });
    }

    if (entry.specType === 'INPUT' && !entry.inputValue?.trim()) {
      messages.push({
        path: `specEntries.${index}.inputValue`,
        message: `${specLabel}為必填`,
      });
    }
  });

  return [
    ...messages,
    ...validateAssemblyOptionsSubmit(values, mode),
  ];
}
